import { Chart } from "@/core/utils/chart-js";
import { ChartConfiguration, ChartTypeRegistry } from "chart.js";
import { Directive } from "vue";

type AppChartHTMLElement = HTMLCanvasElement & {
    _appChartInstance?: Chart;
    _appChartDispose: () => void;
};

export type AppChartOptions<
    ChartType extends keyof ChartTypeRegistry = keyof ChartTypeRegistry,
> = {
    initialConfig: Omit<ChartConfiguration<ChartType>, "options" | "data">;
    options: Chart<ChartType>["options"];
    data: Chart<ChartType>["data"];
};

const updateChart = (el: AppChartHTMLElement, options: AppChartOptions) => {
    if (el._appChartInstance) {
        el._appChartInstance.options = options.options;
        el._appChartInstance.data = options.data;
        el._appChartInstance.update();
        el._appChartInstance.resize();
    }
};

const createChart = (el: AppChartHTMLElement, options: AppChartOptions) => {
    const ctx = el.getContext("2d");
    if (ctx) {
        el._appChartInstance = new Chart(ctx, {
            ...options.initialConfig,
            options: options.options,
            data: options.data,
        });

        const updateChart_ = () => {
            updateChart(el, options);
        };

        const observer = new ResizeObserver((entries) => {
            window.requestAnimationFrame(() => {
                if (!Array.isArray(entries) || !entries.length) {
                    return;
                }

                updateChart_();
            });
        });

        observer.observe(el);

        el._appChartDispose = () => {
            observer.disconnect();
            el._appChartInstance?.destroy();
        };
    }
};

export const appChartDirective: Directive<AppChartHTMLElement, AppChartOptions> = {
    mounted(el, bindings) {
        createChart(el, bindings.value);
    },
    unmounted(el) {
        if (el._appChartDispose) {
            el._appChartDispose();
        }
    },
    updated(el, bindings) {
        updateChart(el, bindings.value);
    },
};
